import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Timer, PhoneCall, Shield, Flame, Heart, Settings, X, Save, Trophy } from "lucide-react";
import MilestoneCelebration, { MILESTONES } from "@/components/MilestoneCelebration";

const STORAGE_KEY = "em_counter_v1";

const TITLE_PRESETS = [
  "Sans me scarifier",
  "Sans alcool",
  "Sans boire",
  "Sans fumer",
  "Sans réseaux sociaux",
  "Sans me couper",
  "Sans crise d'angoisse",
];

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function save(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Storage disabled
  }
}

function diffParts(fromIso) {
  const start = new Date(fromIso).getTime();
  const now = Date.now();
  let ms = Math.max(0, now - start);
  const days = Math.floor(ms / 86400000);
  ms -= days * 86400000;
  const hours = Math.floor(ms / 3600000);
  ms -= hours * 3600000;
  const minutes = Math.floor(ms / 60000);
  ms -= minutes * 60000;
  const seconds = Math.floor(ms / 1000);
  return { days, hours, minutes, seconds };
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function SobrietyCounter() {
  const [data, setData] = useState(() => load() || null);
  const [tick, setTick] = useState(0);
  const [editing, setEditing] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const timerRef = useRef(null);

  // Setup form state
  const [form, setForm] = useState({
    title: "Sans me scarifier",
    contact_name: "",
    contact_phone: "",
    start_iso: new Date().toISOString(),
  });

  useEffect(() => {
    timerRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    if (data) {
      setForm({
        title: data.title,
        contact_name: data.contact_name || "",
        contact_phone: data.contact_phone || "",
        start_iso: data.start_iso,
      });
    }
  }, [data]);

  const startCounter = () => {
    if (!form.title.trim()) {
      toast.error("Choisis un titre pour ton compteur.");
      return;
    }
    const next = {
      title: form.title.trim(),
      contact_name: form.contact_name.trim(),
      contact_phone: form.contact_phone.trim(),
      start_iso: form.start_iso || new Date().toISOString(),
      resets_count: 0,
      hold_total: 0,
      hold_last_date: null,
      created_at: new Date().toISOString(),
    };
    save(next);
    setData(next);
    setEditing(false);
    toast.success("Compteur lancé. Un jour à la fois.");
    // Anonymous public stat ping (fire-and-forget)
    import("@/lib/api").then(({ api }) => {
      api.post("/public/counter-started").catch(() => {});
    });
  };

  const saveSettings = () => {
    if (!form.title.trim()) {
      toast.error("Le titre ne peut pas être vide.");
      return;
    }
    const next = {
      ...data,
      title: form.title.trim(),
      contact_name: form.contact_name.trim(),
      contact_phone: form.contact_phone.trim(),
    };
    save(next);
    setData(next);
    setEditing(false);
    toast.success("Compteur mis à jour.");
  };

  const buildSmsHref = () => {
    const phone = (data.contact_phone || "").replace(/\s+/g, "");
    const body = encodeURIComponent(
      `Hey, c'est moi. Je passe un moment difficile et j'ai touché à mon compteur "${data.title}". Si tu peux me répondre ou m'appeler, ça m'aiderait. Merci d'être là. — Envoyé via L'Équipe & Mental.`
    );
    return `sms:${phone}?body=${body}`;
  };

  const doReset = () => {
    const next = {
      ...data,
      start_iso: new Date().toISOString(),
      resets_count: (data.resets_count || 0) + 1,
      last_celebrated_days: 0,
    };
    save(next);
    setData(next);
    setConfirmReset(false);

    // Trigger SMS to trusted contact (opens native SMS app)
    if (data.contact_phone) {
      try {
        window.location.href = buildSmsHref();
      } catch {}
      toast.success(`Alerte prête pour ${data.contact_name || "ton proche"}. Compteur relancé.`);
    } else {
      toast(
        "Compteur relancé. Pense à ajouter un contact de confiance pour une prochaine fois."
      );
    }
  };

  const onCelebrationDismissed = (celebratedDays) => {
    const next = { ...data, last_celebrated_days: celebratedDays };
    save(next);
    setData(next);
  };

  const holdOn = () => {
    const today = todayIso();
    if (data.hold_last_date === today) {
      toast("Déjà célébré aujourd'hui. Reviens demain, fier·e de toi.");
      return;
    }
    const next = {
      ...data,
      hold_total: (data.hold_total || 0) + 1,
      hold_last_date: today,
    };
    save(next);
    setData(next);
    toast.success("Bravo. Un jour de plus, c'est un jour de gagné. 🫶");
    // Anonymous public stat ping (fire-and-forget)
    import("@/lib/api").then(({ api }) => {
      api.post("/public/hold").catch(() => {});
    });
  };

  // Setup screen
  if (!data) {
    return (
      <section id="compteur" className="py-16 lg:py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <span className="em-eyebrow">Mon compteur</span>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
            Un compteur personnel, à ton image.
          </h2>
          <p className="mt-3 text-slate-300 max-w-2xl">
            Combien de temps as-tu tenu ? Peu importe ce que tu veux arrêter, le temps te
            donnera raison. Et si tu craques, un proche peut être prévenu automatiquement.
          </p>

          <div className="mt-10 em-card p-6 lg:p-8" data-testid="counter-setup">
            <h3 className="text-xl font-semibold text-white font-[Outfit]">Configurer mon compteur</h3>
            <div className="mt-5 grid gap-4">
              <div>
                <label className="text-sm text-slate-300">Titre du compteur</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex. : Sans me scarifier"
                  data-testid="counter-title"
                  className="mt-1 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                  maxLength={60}
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  {TITLE_PRESETS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setForm({ ...form, title: t })}
                      className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-slate-300"
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-slate-300">Prénom du proche (facultatif)</label>
                  <input
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    placeholder="Ex. : Alex"
                    data-testid="counter-contact-name"
                    className="mt-1 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                    maxLength={40}
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-300">Numéro de ce proche (facultatif)</label>
                  <input
                    type="tel"
                    value={form.contact_phone}
                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                    placeholder="Ex. : 06 12 34 56 78"
                    data-testid="counter-contact-phone"
                    className="mt-1 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                    maxLength={25}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-300">Date de départ</label>
                <input
                  type="datetime-local"
                  value={form.start_iso.slice(0, 16)}
                  onChange={(e) => {
                    const v = e.target.value;
                    const iso = v ? new Date(v).toISOString() : new Date().toISOString();
                    setForm({ ...form, start_iso: iso });
                  }}
                  data-testid="counter-start"
                  className="mt-1 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Si tu as déjà tenu quelques jours, choisis ta date de départ réelle.
                </p>
              </div>
              <div className="flex items-start gap-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-sm text-emerald-100">
                <Shield className="w-4 h-4 mt-0.5 shrink-0 text-emerald-300" />
                <p>
                  Toutes ces informations restent <strong>sur ton appareil</strong>. Le SMS
                  d'alerte s'ouvrira depuis <strong>ton téléphone</strong>, tu valideras avant envoi.
                </p>
              </div>
              <button
                onClick={startCounter}
                data-testid="counter-start-btn"
                className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-full"
              >
                <Timer className="w-4 h-4" /> Lancer mon compteur
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const { days, hours, minutes, seconds } = diffParts(data.start_iso);
  const reachedMilestones = MILESTONES.filter((m) => m.days <= days);
  const nextMilestone = MILESTONES.find((m) => m.days > days);

  return (
    <section id="compteur" className="py-16 lg:py-20 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <span className="em-eyebrow">Mon compteur</span>
        <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
          {data.title}
        </h2>
        <p className="mt-2 text-slate-400">
          Chaque seconde compte. Reviens ici dès que tu as besoin de voir le chemin parcouru.
        </p>

        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          {/* Big counter card */}
          <div
            className="lg:col-span-8 relative em-card p-8 lg:p-10 overflow-hidden"
            data-testid="counter-display"
            style={{
              background:
                "radial-gradient(600px 400px at 85% 0%, rgba(16, 185, 129, 0.18), transparent 60%), linear-gradient(180deg, rgba(30, 41, 59, 0.95), rgba(15, 23, 42, 0.95))",
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-xs font-medium">
                <Flame className="w-3.5 h-3.5" /> En cours — bravo
              </div>
              <button
                onClick={() => setEditing(true)}
                data-testid="counter-settings-btn"
                className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5"
              >
                <Settings className="w-3.5 h-3.5" /> Modifier
              </button>
            </div>

            <div className="mt-8 grid grid-cols-4 gap-3 lg:gap-5 text-center" data-testid="counter-values">
              {[
                { v: days, label: "jours" },
                { v: hours, label: "heures" },
                { v: minutes, label: "minutes" },
                { v: seconds, label: "secondes" },
              ].map(({ v, label }) => (
                <div key={label} className="rounded-2xl bg-black/30 border border-white/5 py-4 lg:py-5">
                  <div className="font-[Outfit] font-bold text-4xl lg:text-6xl text-white tabular-nums tracking-tight">
                    {String(v).padStart(2, "0")}
                  </div>
                  <div className="mt-1 text-[11px] lg:text-xs text-slate-400 uppercase tracking-widest">
                    {label}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={holdOn}
                data-testid="counter-hold"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold px-5 py-4 rounded-full shadow-lg shadow-emerald-500/20"
              >
                <Heart className="w-5 h-5" /> Je tiens bon
              </button>
              <button
                onClick={() => setConfirmReset(true)}
                data-testid="counter-reset"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-rose-500/90 hover:bg-rose-600 text-white font-semibold px-5 py-4 rounded-full"
              >
                <Timer className="w-5 h-5" /> J'ai craqué — recommencer
              </button>
            </div>
          </div>

          {/* Side info */}
          <aside className="lg:col-span-4 grid gap-4">
            <div className="em-card p-6">
              <h4 className="text-white font-semibold font-[Outfit]">Mon proche de confiance</h4>
              {data.contact_phone ? (
                <>
                  <p className="mt-2 text-slate-300">
                    {data.contact_name || "Contact"} — <span className="text-slate-400">{data.contact_phone}</span>
                  </p>
                  <a
                    href={`tel:${data.contact_phone.replace(/\s+/g, "")}`}
                    data-testid="counter-call-trusted"
                    className="mt-4 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2.5 rounded-full"
                  >
                    <PhoneCall className="w-4 h-4" /> L'appeler maintenant
                  </a>
                </>
              ) : (
                <>
                  <p className="mt-2 text-sm text-slate-400">
                    Pas encore de proche enregistré. Ajoute un numéro et tu pourras le prévenir
                    en un clic.
                  </p>
                  <button
                    onClick={() => setEditing(true)}
                    className="mt-4 inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-4 py-2.5 rounded-full text-sm"
                  >
                    <Settings className="w-4 h-4" /> Ajouter un contact
                  </button>
                </>
              )}
            </div>

            <div className="em-card p-6">
              <div className="flex items-center gap-2 text-white font-semibold font-[Outfit]">
                <Trophy className="w-4 h-4 text-amber-300" /> Mes badges
              </div>
              {reachedMilestones.length === 0 ? (
                <p className="mt-3 text-sm text-slate-400">
                  Ton premier badge ({MILESTONES[0].tier}) t'attend à {MILESTONES[0].label}.
                </p>
              ) : (
                <div className="mt-4 grid grid-cols-2 gap-2" data-testid="counter-milestones">
                  {reachedMilestones.map((m) => (
                    <div
                      key={m.days}
                      className="rounded-xl border p-3 flex items-center gap-3"
                      style={{
                        background: `linear-gradient(180deg, ${m.color}22, rgba(15, 23, 42, 0.8))`,
                        borderColor: `${m.color}55`,
                      }}
                      title={`${m.tier} — ${m.label}`}
                    >
                      <div
                        className={`w-10 h-10 rounded-full bg-gradient-to-br ${m.gradient} grid place-items-center shrink-0`}
                        style={{ boxShadow: `0 0 18px ${m.color}44` }}
                      >
                        <span className="text-xl">{m.emoji}</span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{m.tier}</div>
                        <div className="text-xs text-slate-400">{m.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {nextMilestone && (
                <div className="mt-4 p-3 rounded-xl bg-white/5 border border-white/5">
                  <p className="text-xs text-slate-400">Prochain badge</p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-xl">{nextMilestone.emoji}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white">
                        {nextMilestone.tier}{" "}
                        <span className="text-slate-400 font-normal">· {nextMilestone.label}</span>
                      </p>
                      <p className="text-xs text-slate-500">
                        Encore {nextMilestone.days - days} jour
                        {nextMilestone.days - days > 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-orange-500 to-emerald-500 transition-all"
                      style={{
                        width: `${Math.min(100, Math.round((days / nextMilestone.days) * 100))}%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="em-card p-6">
              <h4 className="text-white font-semibold font-[Outfit]">Mes statistiques</h4>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-xl bg-white/5 border border-white/5 p-3">
                  <div className="text-2xl font-[Outfit] font-bold text-emerald-300">
                    {data.hold_total || 0}
                  </div>
                  <div className="text-xs text-slate-400">célébrations</div>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/5 p-3">
                  <div className="text-2xl font-[Outfit] font-bold text-slate-200">
                    {data.resets_count || 0}
                  </div>
                  <div className="text-xs text-slate-400">
                    reprise{(data.resets_count || 0) > 1 ? "s" : ""}
                  </div>
                </div>
              </div>
              <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                Recommencer n'est pas échouer. C'est juste repartir avec un peu plus d'expérience.
              </p>
            </div>
          </aside>
        </div>
      </div>

      {/* Confirm reset modal */}
      {confirmReset && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-5"
          data-testid="counter-confirm-modal"
          onClick={(e) => e.target === e.currentTarget && setConfirmReset(false)}
        >
          <div className="em-card max-w-md w-full p-6 lg:p-8">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-xl font-semibold text-white font-[Outfit]">On souffle d'abord ?</h3>
              <button
                onClick={() => setConfirmReset(false)}
                className="p-2 rounded-full hover:bg-white/10"
                aria-label="Fermer"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
            <p className="mt-3 text-slate-300">
              Avant de remettre le compteur à zéro, respire 10 secondes.
              {data.contact_phone ? (
                <>
                  {" "}
                  Si tu continues, un SMS pré-rempli s'ouvrira pour prévenir{" "}
                  <strong className="text-white">
                    {data.contact_name || "ton proche"}
                  </strong>
                  .
                </>
              ) : (
                " Un petit message : ajouter un contact la prochaine fois t'aidera à être moins seul·e dans ces moments."
              )}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setConfirmReset(false)}
                data-testid="counter-cancel-reset"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold px-5 py-3 rounded-full"
              >
                <Heart className="w-4 h-4" /> Je tiens, j'annule
              </button>
              <button
                onClick={doReset}
                data-testid="counter-confirm-reset"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-rose-500/90 hover:bg-rose-600 text-white font-semibold px-5 py-3 rounded-full"
              >
                {data.contact_phone ? "Envoyer le SMS & relancer" : "Recommencer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-5"
          onClick={(e) => e.target === e.currentTarget && setEditing(false)}
        >
          <div className="em-card max-w-lg w-full p-6 lg:p-8">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-xl font-semibold text-white font-[Outfit]">Mon compteur</h3>
              <button onClick={() => setEditing(false)} className="p-2 rounded-full hover:bg-white/10" aria-label="Fermer">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
            <div className="mt-5 grid gap-4">
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Titre du compteur"
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                maxLength={60}
              />
              <div className="grid md:grid-cols-2 gap-3">
                <input
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  placeholder="Prénom du proche"
                  className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                  maxLength={40}
                />
                <input
                  type="tel"
                  value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  placeholder="Numéro de téléphone"
                  className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500"
                  maxLength={25}
                />
              </div>
              <button
                onClick={saveSettings}
                data-testid="counter-save-settings"
                className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-3 rounded-full"
              >
                <Save className="w-4 h-4" /> Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      <MilestoneCelebration data={data} onDismiss={onCelebrationDismissed} />
    </section>
  );
}
